import { FC, useEffect, useState } from 'react';
import classnames from 'classnames';
import { translate } from 'translations';
import { Divider, Icon, LearningPanelNotification, SmallLoader } from 'learning-panel-components';
import SelectLanguageContainer from './selectLanguageContainer';
import DeleteAccountContainer from './deleteAccountContainer/deleteAccountContainer';
import ProfilePictureContainer from './profilePicture/profilePictureContainer';
import ChangePasswordContainer from './changePasswordContainer';
import SelectPrimaryCourseContainer from './primaryCourse/selectPrimaryCourseContainer';
import NicknameContainer from './nicknameContainer';
import EmailNotificationsContainer from './emailNotificationsContainer';
import styles from './accountSettings.module.scss';

const AccountSettings: FC<AccountSettingsProps> = (props: AccountSettingsProps) => {
    const {
        isLoading,
        onClose,
        setIsLoading,
        isEmailNotificationsEnabled,
        isPrimaryCourseEnabled,
        isSso,
        isPLG,
        loginIntegration,
        isError,
        onRetry
    } = props;

    const [showSuccessNotification, setShowSuccessNotification] = useState<boolean>(false);
    const [notificationMessage, setNotificationMessage] = useState<string>('');
    const [showChangePassword, setShowChangePassword] = useState<boolean>(false);
    const [email, setEmail] = useState<string>('');

    useEffect(() => {
        const timer = setTimeout(() => setShowSuccessNotification(false), 50000);
        return () => clearTimeout(timer);
    }, [showSuccessNotification]);

    const renderContent = () => {
        if (isError) {
            return (
                <div className={styles['error-state']}>
                    <div className={styles['error-message']}>{translate('SomethingWentWrong')}</div>
                    <div
                        className={styles['retry-button']}
                        onClick={() => onRetry && onRetry()}
                        id="account-settings-retry"
                    >
                        {translate('TryAgain')}
                    </div>
                </div>
            );
        }

        if (isLoading) {
            return <SmallLoader size="85px" className={styles.loader} />;
        }

        return (
            <>
                <div className={styles['profile-picture']}>
                    <ProfilePictureContainer
                        isLoading={isLoading}
                        setIsLoading={setIsLoading}
                        setShowSuccessNotification={setShowSuccessNotification}
                        setNotificationMessage={setNotificationMessage}
                    />
                </div>
                <NicknameContainer
                    setShowSuccessNotification={setShowSuccessNotification}
                    setNotificationMessage={setNotificationMessage}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                    setEmail={setEmail}
                />
                <div className={styles['bottom-wrapper']}>
                    <div className={styles['item-wrapper']}>
                        <SelectPrimaryCourseContainer
                            setShowSuccessNotification={setShowSuccessNotification}
                            setNotificationMessage={setNotificationMessage}
                        />
                    </div>
                    <div className={styles['item-wrapper']}>
                        <EmailNotificationsContainer
                            setShowSuccessNotification={setShowSuccessNotification}
                            setNotificationMessage={setNotificationMessage}
                        />
                    </div>
                    {!isSso && loginIntegration === null && (
                        <div className={styles['password-wrapper']}>
                            <div
                                className={classnames(styles['change-password'], {
                                    [styles['close-arrow']]: !isEmailNotificationsEnabled && !isPrimaryCourseEnabled
                                })}
                                onClick={() => setShowChangePassword(true)}
                                id="update-password"
                            >
                                <div>{translate('ChangePassword')}</div>
                                <Icon className={styles['arrow-icon']} name="arrowRight" />
                            </div>
                            <div className={styles.space}>
                                <Divider color="rgba(255, 255, 255, 0.25)" />
                            </div>
                        </div>
                    )}
                    {isPLG && (
                        <div className={styles.delete}>
                            <DeleteAccountContainer />
                        </div>
                    )}
                </div>
            </>
        );
    };

    return (
        <div className={styles['background-wrapper']}>
            {showChangePassword ? (
                <ChangePasswordContainer
                    onClose={onClose}
                    onBack={() => setShowChangePassword(false)}
                    setShowSuccessNotification={setShowSuccessNotification}
                    setNotificationMessage={setNotificationMessage}
                />
            ) : (
                <div
                    className={classnames(styles['account-settings-wrapper'], {
                        [styles['small-wrapper']]: !isEmailNotificationsEnabled
                    })}
                    style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/accountSettings.svg)` }}
                >
                    <div className={styles['top-wrapper']}>
                        <div className={styles.title}>
                            <div>{translate('AccountSettings')}</div>
                            <div className={styles.email}>{email}</div>
                        </div>
                        <div className={styles['close-language-wrapper']}>
                            {!isLoading && !isError && (
                                <div className={styles['language-wrapper']}>
                                    <SelectLanguageContainer
                                        setShowSuccessNotification={setShowSuccessNotification}
                                        setNotificationMessage={setNotificationMessage}
                                    />
                                </div>
                            )}
                            <Icon name="cancel" onClick={() => onClose()} className={styles.cancel} />
                        </div>
                    </div>
                    {renderContent()}
                </div>
            )}
            <div
                className={classnames(styles['notification-popup'], {
                    [styles['notification-small-wrapper']]: !isEmailNotificationsEnabled && !showChangePassword
                })}
            >
                {showSuccessNotification && notificationMessage && (
                    <LearningPanelNotification
                        notificationText={notificationMessage}
                        variant="almostTransparent"
                        fullWidth
                        handleClose={() => setShowSuccessNotification(false)}
                    />
                )}
            </div>
        </div>
    );
};

export interface AccountSettingsProps {
    onClose: Function;
    setIsLoading: Function;
    isLoading: boolean;
    isEmailNotificationsEnabled?: boolean;
    isPrimaryCourseEnabled?: boolean;
    isSso?: boolean;
    isPLG?: boolean;
    loginIntegration?: string;
    isError?: boolean;
    onRetry?: Function;
}

export default AccountSettings;
